import { Activity, Watch } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/cn";
import type { WearableDeltasSection } from "@/hooks/api";

export function WearableDataCard({
  section,
}: {
  section: WearableDeltasSection;
}) {
  const source = [...new Set(section.rows.flatMap((row) => row.nowSources ?? []))].join(", ");
  const amino = section.aminoAge;
  const dateRange = formatDateRange(section);

  return (
    <Card>
      <CardContent className="px-3 py-2.5 space-y-2">
        {/* Header */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <Watch className="h-3.5 w-3.5 text-admin-info shrink-0" />
            <span className="text-[11px] font-medium text-admin-text-primary truncate">
              Wearables · {dateRange}
            </span>
          </div>
          <span className="text-[10px] text-admin-text-subtle shrink-0">{source}</span>
        </div>

        {/* Amino age — inline single line */}
        {amino?.now != null && (
          <>
            <div className="flex items-baseline gap-2 flex-wrap text-[11px]">
              <span className="text-admin-text-subtle">Amino age:</span>
              <span className="font-semibold text-admin-text-primary">
                {amino.now.toFixed(1)}
              </span>
              {amino.then != null && (
                <span className="text-admin-text-subtle">
                  (forrige {amino.then.toFixed(1)},{" "}
                  <span className={amino.now - amino.then < 0 ? "text-admin-mint-400" : "text-admin-warn-400"}>
                    {signed(amino.now - amino.then)} år
                  </span>
                  )
                </span>
              )}
              {amino.ageDifferenceNow != null && (
                <span className={
                  amino.ageDifferenceNow < 0
                    ? "text-admin-mint-400"
                    : amino.ageDifferenceNow > 0
                      ? "text-admin-warn-400"
                      : "text-admin-text-subtle"
                }>
                  — {ageDiffLabel(amino.ageDifferenceNow)}
                </span>
              )}
            </div>
            {amino.drivers.length > 0 && (
              <div className="text-[10px] text-admin-text-subtle">
                {amino.drivers.map((d, i) => (
                  <span key={d.id}>{i > 0 ? " · " : ""}{d.name} {signed(d.contribution)}</span>
                ))}
              </div>
            )}
          </>
        )}

        {section.mode === "delta" && section.previousHadNoData && (
          <p className="text-[10px] text-admin-text-subtle">
            Ingen data fra forrige test — kun nåværende vindu.
          </p>
        )}

        {/* Metrics table */}
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-admin-card-border">
                <th className="text-left font-normal text-admin-text-subtle pb-1 pr-2 w-[38%]">Metrikk</th>
                {section.mode === "delta" && section.previousVisitDate != null ? (
                  <>
                    <th className="text-left font-normal text-admin-text-subtle pb-1 pr-2">Da → Nå</th>
                    <th className="text-left font-normal text-admin-text-subtle pb-1 pr-2">Δ</th>
                  </>
                ) : (
                  <th className="text-left font-normal text-admin-text-subtle pb-1 pr-2">Verdi</th>
                )}
                <th className="text-left font-normal text-admin-text-subtle pb-1 pr-2">Ref.</th>
                <th className="text-right font-normal text-admin-text-subtle pb-1">Dkg.</th>
              </tr>
            </thead>
            <tbody>
              {section.rows.map((r) => (
                <WearableRow
                  key={r.key}
                  row={r}
                  mode={section.mode}
                  showThenColumn={section.mode === "delta" && section.previousVisitDate != null}
                  previousHadNoData={section.previousHadNoData}
                />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function WearableRow({
  row,
  mode,
  showThenColumn,
  previousHadNoData,
}: {
  row: WearableDeltasSection["rows"][number];
  mode: "delta" | "baseline";
  showThenColumn: boolean;
  previousHadNoData: boolean;
}) {
  const lowCoverage = isLowCoverage(row);
  return (
    <tr className="border-b border-admin-card-border/40 last:border-0">
      <td className="py-0.5 pr-2 text-admin-text-secondary">{row.label}<div className="text-[10px] text-admin-text-subtle">{row.nowSources?.join(", ")}{showThenColumn && row.thenSources?.length ? ` · Da: ${row.thenSources.join(", ")}` : ""}</div></td>
      {showThenColumn ? (
        <>
          <td className="py-0.5 pr-2 tabular-nums">
            {row.now != null
              ? row.then != null
                ? `${fv(row.then, row)} → ${fv(row.now, row)}${row.unit ? ` ${row.unit}` : ""}`
                : `— → ${fv(row.now, row)}${row.unit ? ` ${row.unit}` : ""}`
              : "—"}
          </td>
          <td className="py-0.5 pr-2 tabular-nums text-admin-text-subtle">{formatChange(row)}</td>
        </>
      ) : (
        <td className="py-0.5 pr-2 tabular-nums">
          {row.now != null ? `${fv(row.now, row)}${row.unit ? ` ${row.unit}` : ""}` : "—"}
        </td>
      )}
      <td className="py-0.5 pr-2 text-admin-text-subtle">
        {row.referenceDisplay}
        {row.referenceUnitOverride ? ` ${row.referenceUnitOverride}` : ""}
      </td>
      <td
        className={cn("py-0.5 text-right font-mono", lowCoverage ? "text-admin-warn-400" : "text-admin-text-subtle")}
        title={lowCoverage ? "Lav dekning" : undefined}
      >
        {coverageLabel(row, mode, previousHadNoData)}
      </td>
    </tr>
  );
}

function isLowCoverage(row: WearableDeltasSection["rows"][number]): boolean {
  if (row.mode === "dense_avg") return row.nowCoverage < row.sliceDays / 2;
  return row.nowCoverage < 5;
}

function coverageLabel(row: WearableDeltasSection["rows"][number], mode: "delta" | "baseline", previousHadNoData: boolean): string {
  const showThen = mode === "delta" && !previousHadNoData;
  if (row.mode === "dense_avg") {
    return showThen
      ? `${row.thenCoverage}/${row.sliceDays}·${row.nowCoverage}/${row.sliceDays}`
      : `${row.nowCoverage}/${row.sliceDays}`;
  }
  return showThen ? `${row.thenCoverage}·${row.nowCoverage}` : `${row.nowCoverage}`;
}

function fv(value: number, row: WearableDeltasSection["rows"][number]): string {
  if (row.key === "steps") return Math.round(value).toLocaleString("nb-NO");
  if (row.unit === "t") return value.toFixed(1);
  if (row.unit === "bpm" || row.unit === "ms" || row.unit === "ml/kg/min") return Math.round(value).toString();
  const r = Math.round(value * 100) / 100;
  return Number.isInteger(r) ? r.toString() : r.toFixed(2).replace(/\.?0+$/, "");
}

function formatChange(row: WearableDeltasSection["rows"][number]): string {
  if (row.then == null || row.now == null) return "";
  const diff = row.now - row.then;
  const s = (n: number) => (n >= 0 ? `+${n}` : `${n}`);
  if (row.unit === "t") { const m = Math.round(diff * 60); return `${s(m)}min`; }
  if (row.unit === "bpm" || row.unit === "ms" || row.unit === "ml/kg/min") return s(Math.round(diff));
  if (row.key === "steps") { if (row.then === 0) return ""; const p = Math.round((diff / row.then) * 100); return `${s(p)}%`; }
  if (row.key === "moderateMinPerWeek" || row.key === "vigorousMinPerWeek") return `${s(Math.round(diff))}min`;
  return s(Math.round(diff));
}

function signed(value: number): string {
  const r = Math.round(value * 10) / 10;
  return r >= 0 ? `+${r}` : `${r}`;
}

function ageDiffLabel(diff: number): string {
  const abs = Math.abs(diff).toFixed(1);
  if (diff < 0) return `yngre ${abs}`;
  if (diff > 0) return `eldre ${abs}`;
  return "lik alder";
}

const MONTHS = ["jan","feb","mar","apr","mai","jun","jul","aug","sep","okt","nov","des"];

function shortDate(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${parseInt(d!)}. ${MONTHS[parseInt(m!) - 1]!}`;
}

function formatDateRange(section: WearableDeltasSection): string {
  if (section.mode === "delta" && section.previousVisitDate != null) {
    return `${shortDate(section.previousVisitDate)} → ${shortDate(section.visitDate)}`;
  }
  const days = section.rows[0]?.sliceDays ?? 30;
  return `siste ${days} d`;
}

WearableDataCard.icon = Activity;
